import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

const ProductLabelSettingsContext = createContext(null);

/** מפרסר תשובת שרת / מטמון — לא מסתמך על !== false בלבד (מחרוזות וכו׳) */
function parseLabelFlag(raw, defaultWhenMissing = true) {
  if (raw === undefined || raw === null) return defaultWhenMissing;
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") return raw.toLowerCase() === "true";
  return defaultWhenMissing;
}

export function ProductLabelSettingsProvider({ children }) {
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("auth_token");

  const { data, isSuccess, isError, error } = useQuery({
    queryKey: ["settings", "ui-labels"],
    queryFn: () => base44.entities.SystemSetting.getUiLabels(),
    staleTime: 0,
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    enabled: hasToken,
  });

  const value = useMemo(() => {
    // רק אחרי טעינה מוצלחת מציגים לפי השרת. לפני כן / בשגיאה — לא מציגים (false) כדי שלא יישארו תוויות ״דמה״ מברירת מחדל שגויה
    if (!hasToken) {
      return {
        showKosherPassoverLabel: false,
        showBakedAfterPassoverLabel: false,
        isLoading: false,
      };
    }

    if (isSuccess && data != null) {
      return {
        showKosherPassoverLabel: parseLabelFlag(data.showKosherPassoverLabel, true),
        showBakedAfterPassoverLabel: parseLabelFlag(data.showBakedAfterPassoverLabel, true),
        isLoading: false,
      };
    }

    if (isError) {
      if (import.meta.env.DEV) {
        console.warn("[ProductLabelSettings] getUiLabels failed:", error);
      }
      return {
        showKosherPassoverLabel: false,
        showBakedAfterPassoverLabel: false,
        isLoading: false,
      };
    }

    // pending / fetching — עדיין אין נתונים אמינים
    return {
      showKosherPassoverLabel: false,
      showBakedAfterPassoverLabel: false,
      isLoading: true,
    };
  }, [hasToken, data, isSuccess, isError, error]);

  return (
    <ProductLabelSettingsContext.Provider value={value}>
      {children}
    </ProductLabelSettingsContext.Provider>
  );
}

export function useProductLabelSettings() {
  const ctx = useContext(ProductLabelSettingsContext);
  if (!ctx) {
    return {
      showKosherPassoverLabel: true,
      showBakedAfterPassoverLabel: true,
      isLoading: false,
    };
  }
  return ctx;
}
